import { supabase } from '@/supabase.config';
import type { Json } from '@/types/supabase';
import type {
  IdentifyResult,
  LockedCi,
  PublicBallot,
  RaceTimeline,
  RoomToken,
  KioskVoter,
  Election,
  ElectionInsert,
  ElectionListItem,
  ElectionState,
  ElectionStats,
  ElectionVoter,
  RaceVote,
  RoundSettings,
} from '../types';

/** Throws the database error; the DETAIL (e.g. the reason a voter is not eligible) goes after a "|" */
function fail(error: { message: string; details?: string | null } | null): asserts error is null {
  if (error) throw new Error(error.details ? `${error.message}|${error.details}` : error.message);
}

// ─── Elections ───────────────────────────────────────────────────────────────

export async function listElections(): Promise<ElectionListItem[]> {
  const { data, error } = await supabase
    .from('elections')
    .select('*, event:events(id, name), election_positions(count), election_races(count)')
    .order('created_at', { ascending: false });
  fail(error);
  return (data ?? []) as unknown as ElectionListItem[];
}

export async function createElection(input: ElectionInsert): Promise<Election> {
  const { data, error } = await supabase.from('elections').insert(input).select().single();
  fail(error);
  return data as Election;
}

export async function updateElection(id: string, patch: Partial<ElectionInsert>): Promise<Election> {
  const { data, error } = await supabase.from('elections').update(patch).eq('id', id).select().single();
  fail(error);
  return data as Election;
}

export async function deleteElection(id: string): Promise<void> {
  const { error } = await supabase.from('elections').delete().eq('id', id);
  fail(error);
}

export async function getElectionState(electionId: string): Promise<ElectionState> {
  const { data, error } = await supabase.rpc('voting_get_election_state', { p_election_id: electionId });
  fail(error);
  return data as unknown as ElectionState;
}

// ─── Event / session pickers ─────────────────────────────────────────────────

export async function listEventsForPicker(): Promise<{ id: string; name: string; start_date: string }[]> {
  const { data, error } = await supabase
    .from('events')
    .select('id, name, start_date')
    .order('start_date', { ascending: false })
    .limit(100);
  fail(error);
  return data ?? [];
}

export async function listSessionsForPicker(eventId: string): Promise<{ id: string; name: string; session_date: string }[]> {
  const { data, error } = await supabase
    .from('event_sessions')
    .select('id, name, session_date')
    .eq('event_id', eventId)
    .order('session_date')
    .order('display_order');
  fail(error);
  return data ?? [];
}

// ─── Candidate pool ──────────────────────────────────────────────────────────

export async function addCandidate(electionId: string, ministerId: string, displayOrder: number): Promise<void> {
  const { error } = await supabase
    .from('election_candidates')
    .insert({ election_id: electionId, minister_id: ministerId, display_order: displayOrder });
  fail(error);
}

export async function setCandidateStatus(candidateId: string, status: 'ACTIVE' | 'WITHDRAWN'): Promise<void> {
  const { error } = await supabase.from('election_candidates').update({ status }).eq('id', candidateId);
  fail(error);
}

export async function removeCandidate(candidateId: string): Promise<void> {
  const { error } = await supabase.from('election_candidates').delete().eq('id', candidateId);
  fail(error);
}

// ─── Positions ───────────────────────────────────────────────────────────────

export async function addPosition(electionId: string, title: string, displayOrder: number): Promise<void> {
  const { error } = await supabase
    .from('election_positions')
    .insert({ election_id: electionId, title, display_order: displayOrder });
  fail(error);
}

export async function removePosition(positionId: string): Promise<void> {
  const { error } = await supabase.from('election_positions').delete().eq('id', positionId);
  fail(error);
}

// ─── Rounds ──────────────────────────────────────────────────────────────────

export async function createRace(
  electionId: string,
  positionId: string,
  candidateIds: string[],
  settings: Partial<RoundSettings>,
): Promise<void> {
  const { error } = await supabase.rpc('voting_create_race', {
    p_election_id: electionId,
    p_position_id: positionId,
    p_candidate_ids: candidateIds,
    p_settings: settings as unknown as Json,
  });
  fail(error);
}

export async function updateRace(raceId: string, candidateIds: string[], settings: Partial<RoundSettings>): Promise<void> {
  const { error } = await supabase.rpc('voting_update_race', {
    p_race_id: raceId,
    p_candidate_ids: candidateIds,
    p_settings: settings as unknown as Json,
  });
  fail(error);
}

export async function deleteRace(raceId: string): Promise<void> {
  const { error } = await supabase.rpc('voting_delete_race', { p_race_id: raceId });
  fail(error);
}

export async function openRace(raceId: string): Promise<void> {
  const { error } = await supabase.rpc('voting_open_race', { p_race_id: raceId });
  fail(error);
}

export async function closeRace(raceId: string): Promise<void> {
  const { error } = await supabase.rpc('voting_close_race', { p_race_id: raceId });
  fail(error);
}

export async function confirmWinner(raceId: string): Promise<void> {
  const { error } = await supabase.rpc('voting_confirm_winner', { p_race_id: raceId });
  fail(error);
}

// ─── Override (ADMIN) ────────────────────────────────────────────────────────

export async function listRaceVotes(raceId: string): Promise<RaceVote[]> {
  const { data, error } = await supabase.rpc('voting_list_race_votes', { p_race_id: raceId });
  fail(error);
  return (data ?? []) as RaceVote[];
}

export async function overrideVote(raceId: string, voterId: string, candidateId: string | null, note: string): Promise<void> {
  const { error } = await supabase.rpc('voting_override_vote', {
    p_race_id: raceId,
    p_voter_id: voterId,
    p_candidate_id: candidateId as string,
    p_note: note,
  });
  fail(error);
}

// ─── Manual voters (elections without event) ─────────────────────────────────

export async function listManualVoters(electionId: string): Promise<ElectionVoter[]> {
  const { data, error } = await supabase
    .from('election_voters')
    .select('*')
    .eq('election_id', electionId)
    .eq('kind', 'MANUAL')
    .order('full_name');
  fail(error);
  return data ?? [];
}

export async function addManualVoter(electionId: string, fullName: string, ci: string): Promise<void> {
  const { error } = await supabase
    .from('election_voters')
    .insert({ election_id: electionId, kind: 'MANUAL', full_name: fullName.trim(), ci: ci.trim() });
  fail(error);
}

export async function setManualVoterEnabled(voterId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.from('election_voters').update({ is_enabled: enabled }).eq('id', voterId);
  fail(error);
}

export async function removeManualVoter(voterId: string): Promise<void> {
  const { error } = await supabase.from('election_voters').delete().eq('id', voterId);
  fail(error);
}

// ─── Kiosk (proxy voting) ────────────────────────────────────────────────────

export async function kioskLookup(electionId: string, query: string): Promise<KioskVoter[]> {
  const { data, error } = await supabase.rpc('voting_kiosk_lookup', { p_election_id: electionId, p_query: query });
  fail(error);
  return (data ?? []) as unknown as KioskVoter[];
}

export async function castKioskVote(input: {
  raceId: string;
  participantId: string | null;
  voterId: string | null;
  /** null = blank vote */
  candidateId: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('voting_cast_kiosk', {
    p_race_id: input.raceId,
    p_participant_id: input.participantId as string,
    p_voter_id: input.voterId as string,
    p_candidate_id: input.candidateId as string,
    p_identity_verified: true,
  });
  fail(error);
}

/** Short-lived URL of the CI photo a guest uploaded when registering (null if none) */
export async function getVoterPhotoUrl(electionId: string, participantId: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('event-doc-upload', {
    body: { action: 'voter-photo', election_id: electionId, participant_id: participantId },
  });
  if (error) return null;
  return (data?.url as string | null) ?? null;
}

// ─── Public voting link ──────────────────────────────────────────────────────

/** Credential failures come back as data ({ok:false}); only unexpected errors throw */
export async function publicIdentify(input: {
  electionId: string;
  roomToken: string;
  ci: string;
  pin?: string;
  badgeToken?: string;
}): Promise<IdentifyResult> {
  const { data, error } = await supabase.rpc('public_voting_identify', {
    p_election_id: input.electionId,
    p_room_token: input.roomToken,
    p_ci: input.ci,
    p_pin: (input.pin ?? null) as string,
    p_badge_token: (input.badgeToken ?? null) as string,
  });
  fail(error);
  return data as unknown as IdentifyResult;
}

export async function publicBallot(sessionToken: string): Promise<PublicBallot> {
  const { data, error } = await supabase.rpc('public_voting_ballot', { p_session_token: sessionToken });
  fail(error);
  return data as unknown as PublicBallot;
}

export async function publicCast(sessionToken: string, raceId: string, candidateId: string | null): Promise<void> {
  const { error } = await supabase.rpc('public_voting_cast', {
    p_session_token: sessionToken,
    p_race_id: raceId,
    p_candidate_id: candidateId as string,
  });
  fail(error);
}

export async function getRoomToken(electionId: string): Promise<RoomToken> {
  const { data, error } = await supabase.rpc('voting_room_token', { p_election_id: electionId });
  fail(error);
  return data as unknown as RoomToken;
}

export async function listLockedCis(electionId: string): Promise<LockedCi[]> {
  const { data, error } = await supabase.rpc('voting_list_locked', { p_election_id: electionId });
  fail(error);
  return (data ?? []) as LockedCi[];
}

export async function unlockCi(electionId: string, ci: string): Promise<void> {
  const { error } = await supabase.rpc('voting_unlock_ci', { p_election_id: electionId, p_ci: ci });
  fail(error);
}

/** Desk: unlock a CI in every election of the event */
export async function unlockCiForEvent(eventId: string, ci: string): Promise<number> {
  const { data, error } = await supabase.rpc('voting_unlock_ci_for_event', { p_event_id: eventId, p_ci: ci });
  fail(error);
  return data ?? 0;
}

/** New PIN for a participant (lost badge, desk) or a manual voter (admin) */
export async function regeneratePin(input: { participantId?: string; voterId?: string }): Promise<string> {
  const { data, error } = await supabase.rpc('voting_regenerate_pin', {
    p_participant_id: (input.participantId ?? null) as string,
    p_voter_id: (input.voterId ?? null) as string,
  });
  fail(error);
  return data as string;
}

/** Votes of a closed round in time buckets (no voter identities), for the bar race */
export async function getRaceTimeline(raceId: string): Promise<RaceTimeline> {
  const { data, error } = await supabase.rpc('voting_race_timeline', { p_race_id: raceId });
  fail(error);
  return data as unknown as RaceTimeline;
}

export async function getElectionStats(electionId: string): Promise<ElectionStats> {
  const { data, error } = await supabase.rpc('voting_election_stats', { p_election_id: electionId });
  fail(error);
  return data as unknown as ElectionStats;
}
